// Kitchen snapshots for online play.
// The host's Game is the whole truth: writeSnapshot() packs everything a guest needs to show it,
// and applySnapshot() makes a guest's mirrored Game look the same (items keep their identity, so
// nothing is rebuilt unless it changed). One-off effects travel separately as events (see Game._ev).
import { DELEGATE_TASKS } from '../game/helper.js';
import { audio } from '../engine/audio.js';
import { Ingredient, Cup, Vessel, Extinguisher, BobaCup, Straw } from '../game/items.js';
import { Guest } from '../game/guest.js';

const r2 = (v) => Math.round(v * 100) / 100;
const r3 = (v) => Math.round(v * 1000) / 1000;

// ------------------------------------------------------------ items
function itemDesc(it) {
  switch (it.kind) {
    case 'ing': return ['i', it.id, it.state, r2(it.prog)];
    case 'cup': return ['c', it.dirty ? 1 : 0, it.count, it.drink || 0, it.fizzy ? 1 : 0, r2(it.fizzProg)];
    case 'vessel': return ['v', it.app, it.contents.join('+'), r2(it.brewed), r2(it.over), it.result || 0, it.stalled ? 1 : 0, it.burnt ? 1 : 0];
    case 'bobacup': return ['b', it.pearls ? 1 : 0, it.drink || 0, it.sealed ? 1 : 0, it.straw ? 1 : 0, r2(it.sealProg)];
    case 'straw': return ['w'];
    default: return ['e'];
  }
}
function makeItem(d) {
  switch (d[0]) {
    case 'i': return new Ingredient(d[1]);
    case 'c': return new Cup();
    case 'v': return new Vessel(d[1]);
    case 'b': return new BobaCup();
    case 'w': return new Straw();
    default: return new Extinguisher();
  }
}
/** Bring a mirrored item's state (and looks) in line with the host's. */
function updateItem(it, d) {
  switch (d[0]) {
    case 'i':
      if (d[2] !== it.state) it.prepare();
      it.prog = d[3];
      break;
    case 'c': {
      const dirty = !!d[1], drink = d[3] || null, fizzy = !!d[4];
      if (dirty !== it.dirty) it.setDirty(dirty);
      if (d[2] !== it.count) {
        it.count = d[2];
        it._rebuild();
      }
      if (drink !== it.drink) {
        if (drink) it.fill(drink);
        else it.empty();
      }
      if (drink && fizzy !== it.fizzy) it.setFizzy(fizzy);
      it.fizzProg = d[5];
      break;
    }
    case 'v': {
      const contents = d[2] ? d[2].split('+') : [];
      const look = contents.join('+') + '|' + d[5] + '|' + d[7];
      it.contents = contents;
      it.brewed = d[3];
      it.over = d[4];
      it.result = d[5] || null;
      it.stalled = !!d[6];
      it.burnt = !!d[7];
      if (it._look !== look) {
        it._look = look;
        it._updateLiquid();
      }
      break;
    }
    case 'b':
      if (d[1] && !it.pearls) it.addPearls();
      if (d[2] && d[2] !== it.drink) it.fill(d[2]);
      if (d[3] && !it.sealed) it.seal();
      if (d[4] && !it.straw) it.addStraw();
      it.sealProg = d[5];
      break;
  }
}

// ------------------------------------------------------------ write (host)
export function writeSnapshot(g) {
  const items = {};
  const ref = (it) => {
    if (!it) return 0;
    items[it.uid] = itemDesc(it);
    return it.uid;
  };
  const stations = [];
  g.stations.forEach((s, i) => {
    const counted = s.kind === 'rack' || s.kind === 'sink';
    if (!s.item && !s.fire && !s.prog && !counted) return;
    const extra = s.kind === 'rack' ? s.clean : s.kind === 'sink' ? s.dirty : s.kind === 'conveyor' ? r2(s.beltT ?? 0.5) : 0;
    stations.push([i, ref(s.item), r2(s.prog || 0), s.fire ? r2(s.fire.hp) : -1, extra]);
  });
  const perks = {};
  for (const k in g.perks) if (g.perks[k] > 0) perks[k] = r2(g.perks[k]);
  const ev = g.events;
  return {
    st: g.state,
    sT: r2(g.stateT),
    tl: g.infinite ? -1 : r2(g.timeLeft), // (an infinite shift has no time left to send: JSON has no Infinity)
    pt: r2(g.played),
    co: g.coins,
    ti: g.tips,
    sv: g.served,
    fa: g.failed,
    sk: g.streak,
    so: g.solo ? 1 : 0,
    bt: g.helpers.map((h) => [h.chef.index, h.chef.thought || '']),
    dg: Object.fromEntries(Object.entries(g.delegateBy).map(([k, d]) => [k, Object.keys(d).filter((t) => d[t])])),
    do: g.botOwner,
    pk: perks,
    dk: r2(g.darkness),
    sh: r2(g.shake),
    fl: r2(g.scene.flash || 0),
    fc: g.scene.flashColor,
    ev: { bridgePhase: ev.bridgePhase, bridgeT: r2(ev.bridgeT) },
    cp: g.chefs.map((c) => c.peer),
    c: g.chefs.map((c) => [r2(c.x), r2(c.z), r2(c.face), r2(c.vx), r2(c.vz), r2(c.dashT), r2(c.squash), r2(c.falling), r2(c.respawn), c.active ? 1 : 0, c.useHeld ? 1 : 0, c.job ? g.stations.indexOf(c.job) : -1, ref(c.held), c.warp]),
    s: stations,
    f: g.floorItems.map((f) => [ref(f.item), r2(f.x), r2(f.z), r2(f.item.node.rotation[1])]),
    p: g.projectiles.map((p) => [ref(p.item), r2(p.x), r2(p.y), r2(p.z), r2(p.vx), r2(p.vy), r2(p.vz)]),
    q: g.guests.map((q) => [q.id, q.seat, q.species.id, q.recipe.id, q.state, r2(q.x), r2(q.z), r2(q.y), r2(q.patience), r2(q.maxPatience), r2(q.beamT), ref(q.cup), r2(q.drinkT), r2(q.happy), r2(q.shake), r2(q.node.rotation[1])]),
    b: g.world.bridges.map((b) => r3(b.ext)),
    items,
  };
}

// ------------------------------------------------------------ apply (guest)
/** Where an item should be: a key that only changes when it moves to a new holder. */
function placeOf(kind, a) {
  return kind + ':' + a;
}
function detach(g, it) {
  const pl = it._place;
  if (!pl) return;
  const [kind, a] = pl.split(':');
  if (kind === 's') {
    const s = g.stations[+a];
    if (s && s.item === it) s.takeItem();
  } else if (kind === 'c') {
    const c = g.chefs[+a];
    if (c && c.held === it) c.release();
  } else if (kind === 'f') {
    const i = g.floorItems.findIndex((f) => f.item === it);
    if (i >= 0) g.floorItems.splice(i, 1);
    g.scene.root.remove(it.node);
  } else if (kind === 'p') {
    const i = g.projectiles.findIndex((p) => p.item === it);
    if (i >= 0) g.projectiles.splice(i, 1);
    g.scene.root.remove(it.node);
  } else if (kind === 'q') {
    const q = g.guests.find((x) => x.id === +a);
    if (q && q.cup === it) {
      q.parts.body.remove(it.node);
      q.cup = null;
    }
  }
  it._place = null;
}

export function applySnapshot(g, s) {
  // --- chefs changed hands (someone joined mid-shift)
  if (s.cp.length !== g.chefs.length || s.cp.some((peer, i) => g.chefs[i].peer !== peer)) g.mirrorCrew(s.cp, s.c);
  // --- items: create what's new, update what changed, drop what's gone
  const map = g.netItems;
  const live = new Set();
  for (const uid in s.items) {
    const d = s.items[uid];
    let it = map.get(uid);
    if (it && (it._net !== d[0] || (d[0] === 'i' && it.id !== d[1]) || (d[0] === 'v' && it.app !== d[1]))) {
      detach(g, it);
      it.dispose();
      it = null;
    }
    if (!it) {
      it = makeItem(d);
      it._net = d[0];
      map.set(uid, it);
    }
    const sig = JSON.stringify(d);
    if (it._sig !== sig) {
      updateItem(it, d);
      it._sig = sig;
    }
    live.add(uid);
  }
  for (const [uid, it] of map) {
    if (live.has(uid)) continue;
    detach(g, it);
    it.dispose();
    map.delete(uid);
  }
  g.vessels = [...map.values()].filter((it) => it.kind === 'vessel');

  // --- guests (before item placement, so their cups have somewhere to go)
  const seenGuests = new Set();
  for (const q of s.q) {
    const [id, seat, sp, rid, state] = q;
    let guest = g.guests.find((x) => x.id === id);
    if (!guest) {
      guest = new Guest(g, seat, rid, { id, species: sp, x: q[5], z: q[6] });
      g.guests.push(guest);
    }
    guest.mirrorState(state, q);
    seenGuests.add(id);
  }
  for (const guest of g.guests.filter((x) => !seenGuests.has(x.id))) guest.dispose();
  g.guests = g.guests.filter((x) => seenGuests.has(x.id));

  // --- where every item is
  const want = new Map();
  for (const st of s.s) if (st[1]) want.set(String(st[1]), placeOf('s', st[0]));
  s.c.forEach((c, i) => c[12] && want.set(String(c[12]), placeOf('c', i)));
  for (const f of s.f) want.set(String(f[0]), placeOf('f', f[0]));
  for (const p of s.p) want.set(String(p[0]), placeOf('p', p[0]));
  for (const q of s.q) if (q[11]) want.set(String(q[11]), placeOf('q', q[0]));
  // first let go of everything that moves, then place it, so two items can swap holders in one step
  for (const [uid, it] of map) if (it._place !== want.get(uid)) detach(g, it);
  for (const st of s.s) {
    const it = st[1] && map.get(String(st[1]));
    const station = g.stations[st[0]];
    if (it && it._place !== placeOf('s', st[0])) {
      if (station.item && station.item !== it) detach(g, station.item);
      station.setItem(it);
      it._place = placeOf('s', st[0]);
    }
  }
  s.c.forEach((c, i) => {
    const it = c[12] && map.get(String(c[12]));
    const chef = g.chefs[i];
    if (it && chef && it._place !== placeOf('c', i)) {
      if (chef.held && chef.held !== it) detach(g, chef.held);
      chef.hold(it);
      it._place = placeOf('c', i);
    }
  });
  for (const f of s.f) {
    const it = map.get(String(f[0]));
    if (!it) continue;
    if (it._place !== placeOf('f', f[0])) {
      g._placeFloor(it, f[1], f[2]);
      it._place = placeOf('f', f[0]);
    }
    it.node.rotation[1] = f[3];
  }
  for (const p of s.p) {
    const it = map.get(String(p[0]));
    if (!it) continue;
    let pr = g.projectiles.find((x) => x.item === it);
    if (!pr) {
      g.scene.add(it.node);
      pr = { item: it, t: 0 };
      g.projectiles.push(pr);
      it._place = placeOf('p', p[0]);
    }
    [pr.x, pr.y, pr.z, pr.vx, pr.vy, pr.vz] = p.slice(1);
  }
  for (const q of s.q) {
    const it = q[11] && map.get(String(q[11]));
    const guest = g.guests.find((x) => x.id === q[0]);
    if (it && guest && it._place !== placeOf('q', q[0])) {
      guest.holdCup(it);
      it._place = placeOf('q', q[0]);
    }
  }

  // --- stations: fires, counts, progress
  const listed = new Map(s.s.map((st) => [st[0], st]));
  g.stations.forEach((station, i) => {
    const st = listed.get(i);
    station.prog = st ? st[2] : 0;
    const hp = st ? st[3] : -1;
    if (hp >= 0 && !station.fire) g._addFire(station);
    if (hp < 0 && station.fire) g._removeFire(station);
    if (station.fire) station.fire.hp = hp;
    if (station.kind === 'rack' && st && st[4] !== station.clean) station.setRackCount(st[4]);
    if (station.kind === 'sink' && st && st[4] !== station.dirty) station.setSinkCount(st[4]);
    if (station.kind === 'conveyor' && st) station.beltT = st[4];
  });

  // --- chefs
  s.c.forEach((c, i) => {
    const chef = g.chefs[i];
    if (!chef) return;
    const [x, z, face, vx, vz, dashT, squash, falling, respawn, active, useHeld, job, , warp] = c;
    chef.active = !!active;
    chef.useHeld = !!useHeld;
    chef.job = job >= 0 ? g.stations[job] : null;
    const locked = falling > 0 || respawn > 0;
    if (chef.local) {
      // our own chef moves on this screen; the host only overrides it when it moved the chef itself
      if (warp !== chef.warp || locked) {
        chef.x = x;
        chef.z = z;
        chef.vx = chef.vz = 0;
        chef.warp = warp;
      }
      if (squash > chef.squash + 0.3) chef.squash = squash;
      chef.netLock = locked;
    } else {
      chef.x = x;
      chef.z = z;
      chef.face = face;
      chef.vx = vx;
      chef.vz = vz;
      chef.dashT = dashT;
      if (squash > chef.squash + 0.3) chef.squash = squash;
    }
    if (chef.respawn > 0 && respawn <= 0) chef.landing = true;
    chef.falling = falling;
    chef.respawn = respawn;
  });

  // --- shift state
  g.state = s.st;
  g.stateT = s.sT;
  if (!g.infinite) g.timeLeft = s.tl;
  if (s.pt != null) g.played = s.pt;
  g.coins = s.co;
  g.tips = s.ti;
  g.served = s.sv;
  g.failed = s.fa;
  g.streak = s.sk;
  g.solo = !!s.so;
  // bot helpers: what each is thinking about, who owns which, and everyone's Delegate choices (ours stay as we set them)
  for (const [i, th] of s.bt || []) if (g.chefs[i]) g.chefs[i].thought = th;
  if (s.do) g.botOwner = s.do;
  for (const k in s.dg || {}) if (!g._prefKey(k)) g.delegateBy[k] = Object.fromEntries(DELEGATE_TASKS.map((t) => [t.id, s.dg[k].includes(t.id)]));
  g.perks = { ...s.pk };
  g.darkTarget = s.dk;
  if (g.shakeEnabled !== false) g.shake = Math.max(g.shake, s.sh);
  // a solar flare's flash: the host's current brightness and colour (it fades on every screen)
  g.scene.flash = s.fl;
  if (s.fc) g.scene.flashColor = s.fc;
  Object.assign(g.events, s.ev);
  g.world.bridges.forEach((b, i) => (b.ext = s.b[i] ?? b.ext));
}

// ------------------------------------------------------------ events
const HUD_EVENTS = ['popText', 'banner', 'perkReveal', 'countdownStep', 'timeUp', 'bumpScore', 'orderNew', 'orderDone'];

/** A HUD stand-in for the host: every call shows here and is also sent to the guests. */
export function recordingHud(hud, g) {
  return new Proxy(hud, {
    get(target, key) {
      const v = target[key];
      if (typeof v !== 'function') return v;
      if (!HUD_EVENTS.includes(key)) return v.bind(target);
      return (...args) => {
        // guests are named by id: the guest's own copy is found on the other side
        const sent = key === 'orderNew' || key === 'orderDone' ? [args[0].id, ...args.slice(1)] : args;
        g._ev('h', [key, sent]);
        return v.apply(target, args);
      };
    },
  });
}

const EMIT_KEYS = ['pos', 'vel', 'life', 'size', 'size1', 'color', 'color1', 'alpha', 'type', 'drag', 'gravity', 'spin'];
export function packEmit(o) {
  return EMIT_KEYS.map((k) => {
    const v = o[k];
    if (v == null) return null;
    return Array.isArray(v) ? v.map(r3) : typeof v === 'number' ? r3(v) : v;
  });
}
function unpackEmit(a) {
  const o = {};
  EMIT_KEYS.forEach((k, i) => a[i] != null && (o[k] = a[i]));
  return o;
}

/** Play the host's one-off effects on this screen. */
export function applyEvents(g, events, me) {
  for (const [type, args, skip] of events) {
    if (skip && skip === me) continue;
    if (type === 's') audio.sfx(args[0], args[1] || {});
    else if (type === 'p') g.scene.particles[args[0]].emit(unpackEmit(args[1]));
    else if (type === 'z') g.stations[args[0]] && (g.stations[args[0]].zapT = 0.4);
    else if (type === 'h') {
      const [key, a] = args;
      if (key === 'orderNew' || key === 'orderDone') {
        const guest = g.guests.find((x) => x.id === a[0]);
        if (guest) g.hud[key](guest, ...a.slice(1));
      } else g.hud[key](...a);
    }
  }
}